// VWAP standard-deviation bands rendered as a lightweight-charts *series primitive*.
// lightweight-charts has no built-in "fill between two lines", so we draw the two
// alpha bands (±1σ inner, ±2σ outer) and their dotted edges ourselves on the pane
// background (behind the candles). Attach with `candleSeries.attachPrimitive(prim)`
// and feed it data via `prim.setData(vwapBands(candles))`.

export type BandPoint = { time: number; vwap: number; u1: number; l1: number; u2: number; l2: number };
export type BandColors = { fill1: string; fill2: string; edge: string };

export class VwapBandsPrimitive {
  private _data: BandPoint[] = [];
  private _series: any = null;
  private _chart: any = null;
  private _requestUpdate: (() => void) | null = null;
  private _paneViews: any[];

  constructor(private _colors: BandColors) {
    this._paneViews = [{
      // Behind the candles but above the grid.
      zOrder: () => 'bottom',
      renderer: () => ({
        draw: () => {},
        drawBackground: (target: any) => this._drawBackground(target),
      }),
    }];
  }

  // ---- ISeriesPrimitive surface (duck-typed) ----
  attached(param: any) {
    this._series = param.series;
    this._chart = param.chart;
    this._requestUpdate = param.requestUpdate;
  }
  detached() { this._series = null; this._chart = null; this._requestUpdate = null; }
  updateAllViews() { /* renderer reads _data live */ }
  paneViews() { return this._paneViews; }

  setData(data: BandPoint[]) {
    this._data = data || [];
    if (this._requestUpdate) this._requestUpdate();
  }

  // Per-tick path: replace the forming bar's point (same time) or append a new
  // bar's point, without rebuilding the whole array like setData does.
  updateLast(p: BandPoint) {
    const d = this._data;
    if (!d.length || d[d.length - 1].time < p.time) d.push(p);
    else d[d.length - 1] = p;
    if (this._requestUpdate) this._requestUpdate();
  }

  private _drawBackground(target: any) {
    const series = this._series;
    const chart = this._chart;
    const data = this._data;
    if (!series || !chart || !data || data.length < 2) return;
    const ts = chart.timeScale();

    try {
      target.useMediaCoordinateSpace((scope: any) => {
        const ctx = scope.context;
        // Map every band point to canvas coords; keep only fully-resolved points so
        // the fill polygons stay well-formed.
        const pts: { x: number; u1: number; l1: number; u2: number; l2: number }[] = [];
        for (const d of data) {
          const x = ts.timeToCoordinate(d.time);
          const u1 = series.priceToCoordinate(d.u1);
          const l1 = series.priceToCoordinate(d.l1);
          const u2 = series.priceToCoordinate(d.u2);
          const l2 = series.priceToCoordinate(d.l2);
          if (x == null || u1 == null || l1 == null || u2 == null || l2 == null) continue;
          pts.push({ x, u1, l1, u2, l2 });
        }
        if (pts.length < 2) return;

        // Outer band fill (l2..u2), then inner band fill (l1..u1) on top → nested look.
        fillBetween(ctx, pts, 'u2', 'l2', this._colors.fill2);
        fillBetween(ctx, pts, 'u1', 'l1', this._colors.fill1);

        // Dotted edges on each band boundary.
        ctx.save();
        ctx.setLineDash([1, 3]);
        ctx.lineWidth = 1;
        ctx.strokeStyle = this._colors.edge;
        for (const key of ['u2', 'l2', 'u1', 'l1'] as const) strokeLine(ctx, pts, key);
        ctx.restore();
      });
    } catch { /* never let a draw error crash the chart */ }
  }
}

function fillBetween(ctx: any, pts: any[], topKey: string, botKey: string, color: string) {
  ctx.beginPath();
  ctx.moveTo(pts[0].x, pts[0][topKey]);
  for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i].x, pts[i][topKey]);
  for (let i = pts.length - 1; i >= 0; i--) ctx.lineTo(pts[i].x, pts[i][botKey]);
  ctx.closePath();
  ctx.fillStyle = color;
  ctx.fill();
}

function strokeLine(ctx: any, pts: any[], key: string) {
  ctx.beginPath();
  ctx.moveTo(pts[0].x, pts[0][key]);
  for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i].x, pts[i][key]);
  ctx.stroke();
}
